import type { Company, Contact, PartyChannel, PartyChannelType } from "../../lib/types";

export type IdentityInput = {
  type: PartyChannelType;
  value: string;
};

export type IdentityMatch = {
  channel: PartyChannel;
  contact?: Contact;
  company?: Company;
};

export function normalizeEmail(value: string) {
  const candidate = value.trim().toLowerCase();
  if (!candidate || !candidate.includes("@") || /\s/.test(candidate)) return null;
  const [local, domain, ...rest] = candidate.split("@");
  if (!local || !domain || rest.length || !domain.includes(".")) return null;
  return `${local}@${domain}`;
}

export function normalizePhone(value: string) {
  const raw = value.trim();
  if (!raw) return null;
  const hasPlus = raw.startsWith("+");
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 8 || digits.length > 15) return null;
  if (hasPlus) return `+${digits}`;
  // The app is Brazil-first, but an unprefixed number stays ambiguous unless
  // it has a valid Brazilian mobile/landline length.
  if (digits.length === 10 || digits.length === 11) return `+55${digits}`;
  return null;
}

export function normalizeInstagram(value: string) {
  const candidate = value.trim().replace(/^https?:\/\/(www\.)?instagram\.com\//i, "").replace(/^@/, "").replace(/\/?(?:\?.*)?$/, "").toLowerCase();
  return /^[a-z0-9._]{1,30}$/.test(candidate) ? candidate : null;
}

export function normalizeChannel(type: PartyChannelType, value: string) {
  if (type === "email") return normalizeEmail(value);
  if (type === "phone") return normalizePhone(value);
  return normalizeInstagram(value);
}

export function normalizeCnpj(value: string) {
  const normalized = value.trim().replace(/[^0-9a-z]/gi, "").toUpperCase();
  return /^[0-9A-Z]{14}$/.test(normalized) ? normalized : null;
}

export function findExactIdentityMatches(
  channels: PartyChannel[],
  input: IdentityInput,
): PartyChannel[] {
  const normalizedValue = normalizeChannel(input.type, input.value);
  if (!normalizedValue) return [];
  return channels.filter((channel) =>
    channel.type === input.type &&
    channel.status === "active" &&
    channel.normalizedValue === normalizedValue,
  );
}

export function selectPrimaryContact(contactIds: string[], requested?: string | null) {
  const unique = [...new Set(contactIds)];
  if (!unique.length) return { contactIds: unique, primaryContactId: null };
  if (requested && unique.includes(requested)) return { contactIds: unique, primaryContactId: requested };
  return { contactIds: unique, primaryContactId: unique[0] };
}

export function companyCnpjDecision(companies: Company[], cnpj: string) {
  const normalizedCnpj = normalizeCnpj(cnpj);
  if (!normalizedCnpj) return { normalizedCnpj: null, exact: undefined, requiresConfirmation: false };
  const exact = companies.find((company) => company.status !== "merged" && company.normalizedCnpj === normalizedCnpj);
  return { normalizedCnpj, exact, requiresConfirmation: Boolean(exact) };
}
